// ─────────────────────────────────────────────────────────────────────────────
// AiChatService
// ─────────────────────────────────────────────────────────────────────────────
// Responsibilities:
//  - Messaging: POST /v1/ai/chat (JSON) and POST /v1/ai/chat/stream (SSE)
//  - Conversation management: list, get, delete via /v1/ai/chat/conversations
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { IAiConversation, IAiConversationSummary, IAiMessage } from '../models/ai-chat.models';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from 'src/app/shared/localstorage.service';

/** Payload sent to the /v1/ai/chat and /v1/ai/chat/stream endpoints. */
interface ChatRequestBody {
  messages: IAiMessage[];
  session_id?: string | null;
}

/** Response from POST /v1/ai/chat (non-streaming). */
export interface ChatResponse {
  confidence: string;
  references: {
    id: number;
    category: string;
    excerpt: string;
    source: string;
  }[];
  text: string;
  session_id?: string;
  status: string;
}

/** Events emitted by sendMessageStream — either a text chunk or a session id. */
export interface StreamEvent {
  type: 'text' | 'session';
  text?: string;
  sessionId?: string;
}

/** Raw conversation item from GET /v1/ai/chat/conversations. */
interface RawConversationSummary {
  id: string;
  title: string;
  created_at: string;
  last_message_at: string;
  preview?: string;
  message_count?: number;
}

/** Raw message from GET /v1/ai/chat/conversations/:id. */
interface RawMessage {
  role: string;
  text?: string;
  content?: string;
  timestamp: string;
}

/** Raw conversation detail from GET /v1/ai/chat/conversations/:id. */
interface RawConversationDetail {
  conversation_id?: string;
  id?: string;
  title: string;
  messages: RawMessage[];
  created_at?: string;
  updated_at?: string;
  session_id?: string;
}

@Injectable({ providedIn: 'root' })
export class AiChatService {

  private readonly baseUrl = environment.BASE_URL_API + 'ai/chat';
  private readonly conversationsUrl = environment.BASE_URL_API + 'ai/chat/conversations';

  /** Emits individual text chunks during streaming responses (bypasses NgRx for performance). */
  readonly streamChunk$ = new Subject<string>();

  constructor(private http: HttpClient) {}

  // ── Conversation management ──────────────────────────────────────────────────

  /** GET /v1/ai/chat/conversations — returns lightweight summaries (no messages). */
  listConversations(): Observable<IAiConversationSummary[]> {
    return this.http
      .get<{ conversations: RawConversationSummary[] }>(this.conversationsUrl)
      .pipe(map((res) => (res.conversations ?? []).map(this.mapSummary)));
  }

  /** GET /v1/ai/chat/conversations/:id — returns full conversation with messages. */
  getConversation(conversationId: string): Observable<IAiConversation> {
    return this.http
      .get<RawConversationDetail>(`${this.conversationsUrl}/${conversationId}`)
      .pipe(map((raw) => this.mapConversationDetail(raw)));
  }

  /** DELETE /v1/ai/chat/conversations/:id. */
  deleteConversation(conversationId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.conversationsUrl}/${conversationId}`);
  }

  // ── Mapping helpers ──────────────────────────────────────────────────────────

  private mapSummary(raw: RawConversationSummary): IAiConversationSummary {
    return {
      id: raw.id,
      title: raw.title,
      createdAt: raw.created_at,
      updatedAt: raw.last_message_at ?? raw.created_at,
      preview: raw.preview,
      messageCount: raw.message_count,
    };
  }

  private mapConversationDetail(raw: RawConversationDetail): IAiConversation {
    return {
      id: raw.conversation_id ?? raw.id ?? '',
      title: raw.title,
      messages: (raw.messages ?? []).map((m) => ({
        role: (m.role === 'assistant' ? 'ai' : m.role) as IAiMessage['role'],
        text: m.text ?? m.content ?? '',
        timestamp: m.timestamp,
      })),
      createdAt: raw.created_at ?? '',
      updatedAt: raw.updated_at ?? raw.created_at ?? '',
      sessionId: raw.session_id ?? null,
    };
  }

  /**
   * Full assistant reply in one response (non-streaming).
   * POST /v1/ai/chat — body: { messages: [...], session_id?: "..." }
   */
  sendMessage(messages: IAiMessage[], sessionId?: string | null): Observable<ChatResponse> {
    const body: ChatRequestBody = { messages };
    if (sessionId) {
      body.session_id = sessionId;
    }
    return this.http.post<ChatResponse>(this.baseUrl, body);
  }

  /**
   * SSE stream of text chunks (Deep Chat format per line: data: { "text": "..." }).
   * POST /v1/ai/chat/stream — uses fetch + ReadableStream (HttpClient does not stream the body).
   *
   * Emits StreamEvent objects: 'text' events for content chunks, 'session' event for the session id.
   */
  sendMessageStream(messages: IAiMessage[], sessionId?: string | null): Observable<StreamEvent> {
    return new Observable<StreamEvent>((subscriber) => {
      const url = `${this.baseUrl}/stream`;
      const controller = new AbortController();

      const body: ChatRequestBody = { messages };
      if (sessionId) {
        body.session_id = sessionId;
      }

      const run = async (): Promise<void> => {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'text/event-stream',
              ...this.getAuthHeader(),
            },
            body: JSON.stringify(body),
            signal: controller.signal,
          });

          if (!response.ok) {
            subscriber.error(new Error(`AI chat stream failed: HTTP ${response.status}`));
            return;
          }

          const reader = response.body?.getReader();
          if (!reader) {
            subscriber.error(new Error('AI chat stream: response has no body'));
            return;
          }

          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              break;
            }
            buffer += decoder.decode(value, { stream: true });
            buffer = this.emitCompleteSseLines(buffer, subscriber);
          }

          buffer += decoder.decode();
          buffer = this.emitCompleteSseLines(buffer, subscriber);
          this.emitFinalSseFragment(buffer, subscriber);

          subscriber.complete();
        } catch (err) {
          if ((err as Error).name === 'AbortError') {
            subscriber.complete();
          } else {
            subscriber.error(err);
          }
        }
      };

      void run();

      return () => controller.abort();
    });
  }

  /** Returns the Authorization header from the current user session. */
  private getAuthHeader(): Record<string, string> {
    try {
      const currentUser = LocalStorageService.get('currentUser');
      if (currentUser?.access_token) {
        return { Authorization: 'Bearer ' + currentUser.access_token };
      }
    } catch { /* no token available */ }
    return {};
  }

  /** Parses full `data:` lines from buffer; returns the remainder (incomplete line). */
  private emitCompleteSseLines(buffer: string, subscriber: { next: (v: StreamEvent) => void }): string {
    const lines = buffer.split('\n');
    const rest = lines.pop() ?? '';
    for (const line of lines) {
      const event = this.parseSseDataLine(line);
      if (event !== undefined) {
        subscriber.next(event);
      }
    }
    return rest;
  }

  /** Handles the last fragment after the stream closes (incomplete line with no trailing \n). */
  private emitFinalSseFragment(buffer: string, subscriber: { next: (v: StreamEvent) => void }): void {
    const event = this.parseSseDataLine(buffer);
    if (event !== undefined) {
      subscriber.next(event);
    }
  }

  private parseSseDataLine(line: string): StreamEvent | undefined {
    const trimmed = line.replace(/\r$/, '').trim();
    if (!trimmed.startsWith('data:')) {
      return undefined;
    }
    const jsonStr = trimmed.slice(5).trim();
    if (!jsonStr || jsonStr === '[DONE]') {
      return undefined;
    }
    try {
      const parsed = JSON.parse(jsonStr) as { text?: string; session_id?: string; error?: string };
      if (parsed.session_id) {
        return { type: 'session', sessionId: parsed.session_id };
      }
      if (parsed.error) {
        return { type: 'text', text: parsed.error };
      }
      return { type: 'text', text: parsed.text };
    } catch {
      return undefined;
    }
  }
}
